import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronsUpDown, Eye } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { AccountDto } from "@cwm/shared";

import { apiFetch, ApiError, setAccessToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { listAllAccounts } from "@/lib/accounts";
import { cn } from "@/lib/cn";
import { useOptionalToast } from "@/components/ui/Toast";
import { Drawer } from "@/components/ui/Drawer";
import { Input } from "@/components/ui/Input";

/**
 * Navbar account switcher. Lists every membership on the current Me; the
 * active one is marked with a check. Selecting a different account:
 *   1. POSTs to `/auth/switch-account` — server updates `User.lastAccountId`
 *      and returns a freshly signed access token whose claims reflect the
 *      new active account.
 *   2. Swaps that token into the api module.
 *   3. Hard-reloads the page so every in-flight query, cache, and pagination
 *      state is re-hydrated under the new scope. A softer refresh would
 *      risk leaking the old account's wallets into the new tenant view.
 *
 * Super admins also see a "Super admin" section with a "View as account…"
 * entry that opens a searchable picker over every tenant account. Picking
 * one POSTs the same endpoint with `mode: "viewAs"` so the new JWT carries
 * the `viewAsAccountId` claim — the rest of the app then renders that
 * account's data as if the super admin were its ADMIN. See ViewAsBanner.
 */
interface SwitchAccountResponse {
  accessToken: string;
}

export interface AccountSwitcherProps {
  /**
   * Compact mode renders a 44×44 square trigger with the active account's
   * initial — used in the mobile header where there's no room for the full
   * "Account / Name" stacked label. The dropdown content is unchanged so the
   * affordance is consistent across viewports.
   */
  compact?: boolean;
}

export function AccountSwitcher({ compact = false }: AccountSwitcherProps = {}) {
  const { memberships, activeAccountId, isSuperAdmin } = useAuth();
  const { toast } = useOptionalToast();
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Super admin with no memberships: render a muted chip / disabled circle
  // (covered below) — but we still want the "View as account…" entry to be
  // reachable, so the early-return below only fires for non-super-admins
  // with zero memberships. Super admins land in the main render path.
  if (memberships.length === 0 && !isSuperAdmin) {
    // Non-super-admin with zero memberships is unreachable in practice:
    // `/auth/me` never returns an authenticated non-super-admin with an
    // empty memberships array because the last-membership delete path in
    // `resolveAccountScope` invalidates the session (token version bump),
    // forcing a re-login that itself fails the "must have ≥1 membership"
    // gate. The branch remains as a defensive fallback so the switcher
    // gracefully renders nothing instead of crashing if that invariant
    // ever slips — don't "simplify" it away.
    return null;
  }

  const active = memberships.find((m) => m.accountId === activeAccountId);
  const label = active
    ? active.accountName
    : isSuperAdmin
      ? "No account — super admin view"
      : "Select account";

  const handleSelect = async (accountId: string) => {
    if (busy || accountId === activeAccountId) return;
    setBusy(true);
    try {
      const { accessToken } = await apiFetch<SwitchAccountResponse>(
        "/auth/switch-account",
        {
          method: "POST",
          body: JSON.stringify({ accountId }),
        },
      );
      setAccessToken(accessToken);
      // Hard reload so every data source re-fetches under the new scope.
      window.location.reload();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.body.message ?? "Could not switch account."
          : "Could not switch account.";
      toast({ title: message, variant: "error" });
      setBusy(false);
    }
  };

  const handleViewAs = async (accountId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const { accessToken } = await apiFetch<SwitchAccountResponse>(
        "/auth/switch-account",
        {
          method: "POST",
          body: JSON.stringify({ accountId, mode: "viewAs" }),
        },
      );
      setAccessToken(accessToken);
      // Same hard-reload approach as the regular switch: every cached query
      // must re-fetch under the impersonated scope. The destination URL is
      // preserved so the super admin sees the impersonated equivalent of
      // wherever they were.
      window.location.reload();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.body.message ?? "Could not enter view-as mode."
          : "Could not enter view-as mode.";
      toast({ title: message, variant: "error" });
      setBusy(false);
    }
  };

  // Compact (mobile) trigger initial — falls back to the super-admin "—"
  // glyph when there's no active membership.
  // Unicode-safe leading-grapheme extraction. `.slice(0, 1)` would split a
  // surrogate pair (emoji, non-BMP) into a lone half and render a replacement
  // glyph — spread the string through an iterator first so we grab the whole
  // code point, then uppercase. Trim upfront so leading whitespace doesn't
  // land as an empty avatar.
  const initial =
    [...(active?.accountName ?? "").trim()][0]?.toUpperCase() ?? "—";

  // The full-width row variant for the empty super-admin case (no membership
  // chosen yet). A real <button> (not a role="note" div) so a super admin with
  // no memberships — whose only route into a tenant is "View as account…"
  // inside this menu — can Tab to it and open on Enter/Space. Styling is
  // unchanged; layout parity keeps the surrounding chrome from shifting.
  const noMembershipChip = (
    <button
      type="button"
      aria-label="Account menu — no active account"
      data-testid="account-switcher-empty"
      className={cn(
        "flex w-full min-h-[44px] items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-left text-sm",
        "transition-colors duration-fast ease hover:bg-surface",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-label">
          Account
        </span>
        <span className="truncate text-sm text-text-muted">
          No account — super admin view
        </span>
      </span>
    </button>
  );

  const noMembershipCompact = (
    <button
      type="button"
      aria-label="Account menu — no active account"
      data-testid="account-switcher-empty"
      className={cn(
        "inline-flex h-11 w-11 items-center justify-center rounded-full",
        "bg-surface-2 text-text-muted",
        "transition-colors duration-fast ease hover:bg-surface",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      <span aria-hidden>—</span>
    </button>
  );

  // Super admin with no memberships: still render the dropdown so the
  // "View as account…" entry is reachable. The trigger uses the muted chip
  // styling so the affordance reads as "no real account yet".
  const triggerEl =
    memberships.length === 0
      ? compact
        ? noMembershipCompact
        : noMembershipChip
      : compact
        ? (
          <button
            type="button"
            aria-label="Switch account"
            data-testid="account-switcher-trigger"
            disabled={busy}
            title={label}
            className={cn(
              "relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md",
              "border border-border bg-surface text-sm font-bold text-text",
              "transition-colors duration-fast ease hover:bg-surface-2",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              "disabled:opacity-60",
            )}
          >
            <span aria-hidden>{initial}</span>
            <ChevronsUpDown
              className="absolute bottom-0.5 right-0.5 h-3 w-3 text-text-muted"
              strokeWidth={1.5}
              aria-hidden
            />
          </button>
        )
        : (
          <button
            type="button"
            aria-label="Switch account"
            data-testid="account-switcher-trigger"
            disabled={busy}
            className={cn(
              "flex w-full min-h-[44px] items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-left text-sm",
              "text-text transition-colors duration-fast ease hover:bg-surface-2",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              "disabled:opacity-60",
            )}
          >
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-label">
                Account
              </span>
              <span className="truncate text-sm text-text">{label}</span>
            </span>
            <ChevronsUpDown
              className="h-4 w-4 shrink-0 text-text-muted"
              strokeWidth={1.5}
              aria-hidden
            />
          </button>
        );

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>{triggerEl}</DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            // Switcher now sits at the TOP of the sidebar (Wave 2 move) and is
            // also used as the mobile header switcher, so the menu should always
            // open downward. `side="top"` caused Radix to fight a collision
            // reflow in both positions.
            side="bottom"
            align="start"
            sideOffset={8}
            className={cn(
              "z-50 min-w-[240px] rounded-md border border-border bg-surface p-1 shadow-elev-2",
              "text-sm text-text",
            )}
          >
            {memberships.map((m) => {
              const current = m.accountId === activeAccountId;
              return (
                <DropdownMenu.Item
                  key={m.accountId}
                  onSelect={(e) => {
                    e.preventDefault();
                    void handleSelect(m.accountId);
                  }}
                  data-testid={`account-switch-${m.accountId}`}
                  className={cn(
                    "flex min-h-[40px] cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none",
                    "data-[highlighted]:bg-surface-2",
                  )}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      current ? "text-accent" : "invisible",
                    )}
                    strokeWidth={1.5}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm">{m.accountName}</span>
                    <span className="text-label lowercase first-letter:uppercase">
                      {m.role}
                    </span>
                  </span>
                </DropdownMenu.Item>
              );
            })}

            {isSuperAdmin && (
              <>
                {memberships.length > 0 && (
                  <DropdownMenu.Separator className="my-1 h-px bg-border" />
                )}
                <DropdownMenu.Label
                  className={cn(
                    "px-3 pt-1 pb-0.5 text-label",
                  )}
                >
                  Super admin
                </DropdownMenu.Label>
                <DropdownMenu.Item
                  onSelect={(e) => {
                    e.preventDefault();
                    setPickerOpen(true);
                  }}
                  data-testid="account-switch-view-as"
                  className={cn(
                    "flex min-h-[40px] cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none",
                    "data-[highlighted]:bg-surface-2",
                  )}
                >
                  <Eye
                    className="h-4 w-4 shrink-0 text-text-muted"
                    strokeWidth={1.5}
                    aria-hidden
                  />
                  <span className="text-sm">View as account…</span>
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Picker is rendered as a sibling of the dropdown so closing the
          dropdown via outside-click doesn't unmount the picker mid-flight. */}
      <ViewAsPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handleViewAs}
        busy={busy}
      />
    </>
  );
}

interface ViewAsPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (accountId: string) => void;
  busy: boolean;
}

/**
 * Searchable account picker. Fetches the full super-admin account list on
 * first open (then keeps it in component state — the list is small and
 * stable enough that a single fetch per session is plenty). Filters
 * client-side by case-insensitive substring on name / id.
 *
 * Picker is a sibling of the dropdown menu so the dropdown can close
 * (via outside-click) without unmounting the picker mid-flight.
 */
function ViewAsPicker({ open, onOpenChange, onPick, busy }: ViewAsPickerProps) {
  const [query, setQuery] = useState("");
  const [accounts, setAccounts] = useState<AccountDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Lazy-fetch on first open. Re-fetches if the user reopens the picker AND
  // the previous fetch failed — successful loads are cached for the rest of
  // the session.
  useEffect(() => {
    if (!open) return;
    if (accounts && !loadError) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listAllAccounts({ includeDisabled: false });
        if (!cancelled) {
          setAccounts(res.items);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError
              ? err.body.message ?? "Could not load accounts."
              : "Could not load accounts.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accounts, loadError]);

  const filtered = useMemo(() => {
    if (!accounts) return [];
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q),
    );
  }, [accounts, query]);

  // Reset the query when the picker closes so reopening it doesn't show a
  // stale filter. Keep the cached account list — re-fetching is wasteful.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="View as account"
      description="Pick a tenant to impersonate. Every action you take while viewing-as is audit-tagged as a super-admin impersonation."
    >
      <div className="flex h-full flex-col gap-3 p-6">
        <Input
          autoFocus
          placeholder="Search by name or id…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search accounts"
          data-testid="view-as-picker-search"
        />
        {loadError && (
          <div
            role="alert"
            className="rounded-md border border-[var(--neg-line)] bg-[var(--neg-bg)] px-3 py-2 text-sm text-text"
          >
            {loadError}
          </div>
        )}
        {!accounts && !loadError && (
          <div className="text-sm text-text-muted">Loading accounts…</div>
        )}
        {accounts && filtered.length === 0 && (
          <div className="text-sm text-text-muted">No accounts match.</div>
        )}
        <ul
          className="flex flex-1 flex-col gap-1 overflow-y-auto"
          aria-label="Accounts"
        >
          {filtered.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                disabled={busy}
                data-testid={`view-as-pick-${a.id}`}
                onClick={() => onPick(a.id)}
                className={cn(
                  "flex w-full min-h-[44px] items-center gap-2 rounded-md border border-transparent bg-surface px-3 py-2 text-left text-sm",
                  "text-text transition-colors duration-fast ease hover:bg-surface-2",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  "disabled:opacity-60",
                )}
              >
                <Eye
                  className="h-4 w-4 shrink-0 text-text-muted"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{a.name}</span>
                  <span className="text-label">
                    {a.id}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Drawer>
  );
}
